<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { api, ApiError } from '@/lib/api'
import { login } from '@/lib/auth'
import { errorText } from '@/lib/errors'
import { LOCALES, setLocale, storedLocale, type Locale } from '@/lib/i18n'
import Banner from '@/ui/Banner.vue'
import Button from '@/ui/Button.vue'
import Field from '@/ui/Field.vue'
import Input from '@/ui/Input.vue'
import Select from '@/ui/Select.vue'
import Logo from '@/ui/Logo.vue'
import { useDebounced } from '@/lib/debounce'
import AuthLayout from './AuthLayout.vue'

const { t, locale } = useI18n()
const ORG_KEY = 'telsellhub.org'
const storedOrg = (): string => { try { return localStorage.getItem(ORG_KEY) ?? '' } catch { return '' } }

/** The org named by the address, if any: then the org is not asked for. */
const addressOrg = ref<{ slug: string; name: string; locale: Locale; logoUrl?: string | null } | null>(null)
/**
 * The address names an org the server does not know, or a suspended one:
 * every login from it is refused, so the form is not offered at all.
 */
const addressRefused = ref<ApiError | null>(null)
const checked = ref(false)
const org = ref(storedOrg())
const username = ref('')
const password = ref('')
/** The error itself, not its text: the text follows a language switch. */
const error = ref<unknown>(null)
const errorMessage = computed(() => (error.value ? errorText(error.value, 'auth', 'auth.failed') : ''))
const refusedMessage = computed(() => (addressRefused.value ? errorText(addressRefused.value, 'auth', 'auth.failed') : ''))
const busy = ref(false)

const askOrg = computed(() => checked.value && !addressOrg.value)

/*
 * The org's name and logo: from the address, or (outside production, where
 * the form names the org) from the code typed, once the typing stops.
 */
const typedBrand = ref<{ name: string; logoUrl: string | null } | null>(null)
const typedCode = useDebounced(() => org.value.trim().toLowerCase(), 400)
const SLUG = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/
let brandRequest = 0
watch(typedCode, async (code) => {
  const mine = ++brandRequest
  if (addressOrg.value || !SLUG.test(code)) { typedBrand.value = null; return }
  try {
    const o = (await api<{ org: { name: string; logoUrl?: string | null } | null }>(`/auth/org?org=${encodeURIComponent(code)}`)).org
    if (mine === brandRequest) typedBrand.value = o ? { name: o.name, logoUrl: o.logoUrl ?? null } : null
  } catch {
    if (mine === brandRequest) typedBrand.value = null
  }
}, { immediate: true })
const brand = computed(() => (addressOrg.value
  ? { name: addressOrg.value.name, logoUrl: addressOrg.value.logoUrl ?? null }
  : typedBrand.value))
const valid = computed(() => username.value.trim() !== '' && password.value !== '' && (!askOrg.value || org.value.trim() !== ''))

onMounted(async () => {
  try {
    addressOrg.value = (await api<{ org: typeof addressOrg.value }>('/auth/org')).org
    if (addressOrg.value && !storedLocale()) await setLocale(addressOrg.value.locale)
  } catch (e) {
    if (e instanceof ApiError && (e.code === 'ORG_NOT_FOUND' || e.code === 'ORG_SUSPENDED')) addressRefused.value = e
    /* otherwise no answer: ask for the org */
  }
  checked.value = true
})

/*
 * The first empty field takes the focus once the form renders. `autofocus`
 * would only work on the first page load, not after a logout or a lost
 * session, when this screen comes back without a reload.
 */
const orgInput = ref<InstanceType<typeof Input> | null>(null)
const userInput = ref<InstanceType<typeof Input> | null>(null)
watch(checked, async (ready) => {
  if (!ready) return
  await nextTick()
  ;(askOrg.value && !org.value ? orgInput : userInput).value?.focus()
}, { immediate: true })

async function submit() {
  if (!valid.value || busy.value) return
  error.value = null
  busy.value = true
  const slug = askOrg.value ? org.value.trim().toLowerCase() : null
  try {
    await login(slug, username.value.trim(), password.value)
    if (slug) { try { localStorage.setItem(ORG_KEY, slug) } catch { /* storage unavailable */ } }
  } catch (e) {
    error.value = e
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <AuthLayout :title="t('auth.hero.title')" :body="t('auth.hero.body')">
    <div class="grid w-full max-w-[380px] animate-rise-in gap-4">
      <div class="flex items-center justify-center gap-2.5">
        <Logo :url="brand?.logoUrl" :size="36" />
        <span class="grid leading-tight">
          <strong class="text-[18px]">{{ brand?.name ?? t('common.appName') }}</strong>
          <span v-if="brand" class="text-[12px] text-muted-foreground">{{ t('common.appName') }}</span>
        </span>
      </div>
      <div v-if="addressRefused" class="grid gap-3 rounded-card border border-border bg-card p-5 shadow-sm">
        <h1 class="text-[16px] font-semibold">{{ t('auth.title') }}</h1>
        <Banner tone="danger">{{ refusedMessage }}</Banner>
        <p class="text-[13px] text-muted-foreground">{{ t('auth.addressRefused') }}</p>
      </div>
      <!-- Rendered once the address is known: the first field must get the focus. -->
      <form v-else-if="checked" class="grid gap-3 rounded-card border border-border bg-card p-5 shadow-sm" @submit.prevent="submit">
        <h1 class="text-[16px] font-semibold">{{ t('auth.title') }}</h1>
        <p v-if="addressOrg" class="text-[13px] text-muted-foreground">{{ t('auth.orgOf', { name: addressOrg.name }) }}</p>
        <Field v-if="askOrg" v-slot="{ id, describedby }" :label="t('auth.org')">
          <!-- The slug reads left to right; its placeholder follows the page. -->
          <Input :id="id" ref="orgInput" v-model="org" autocomplete="organization" :placeholder="t('auth.orgPlaceholder')"
                 :aria-describedby="describedby" class="ltr-value rtl:placeholder:[direction:rtl]" />
        </Field>
        <Field v-slot="{ id, describedby }" :label="t('auth.username')">
          <Input :id="id" ref="userInput" v-model="username" autocomplete="username" class="ltr-value" :aria-describedby="describedby" />
        </Field>
        <Field v-slot="{ id, describedby }" :label="t('auth.password')">
          <Input :id="id" v-model="password" type="password" autocomplete="current-password" :aria-describedby="describedby" />
        </Field>
        <Button type="submit" block :loading="busy" :disabled="!valid">
          {{ busy ? t('auth.submitting') : t('auth.submit') }}
        </Button>
        <Banner v-if="errorMessage" tone="danger">{{ errorMessage }}</Banner>
      </form>
      <div class="flex items-center justify-between gap-3 text-[12px] text-muted-foreground">
        <span>{{ t('auth.footer') }}</span>
        <Select :model-value="locale" :aria-label="t('shell.language.label')" class="h-8 w-auto"
                @update:model-value="(l: string) => setLocale(l as Locale, true)">
          <option v-for="l in LOCALES" :key="l" :value="l">{{ t(`shell.language.${l}`) }}</option>
        </Select>
      </div>
    </div>
  </AuthLayout>
</template>
